import type { RootState } from 'src/redux/store';

import { useMemo, useCallback } from 'react';

import { useAppSelector } from 'src/hook/hooks';
import {
  useGetBillingProfileQuery,
  useSaveBillingProfileMutation,
} from 'src/pages/tenants/slice/tenantsApi';

import { toBillingProfilePayload, getMissingBillingProfileFields } from './billing-profile.config';

import type { BillingProfileFormValues } from './billing-profile.config';

// ----------------------------------------------------------------------

type Options = {
  /** Skip loading, e.g. for Super Admin acting on another tenant. */
  skip?: boolean;
  /**
   * Tenant to load, for Super Admin on a tenant's page; defaults to the user's own tenant.
   * The request itself targets the tenant sent in the x-tenant-id header, which the tenant
   * page sets; this id keeps the cache separate per tenant.
   */
  tenantId?: string;
};

export function useBillingProfile({ skip = false, tenantId: selectedTenantId }: Options = {}) {
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);
  const ownTenantId = loginApiData && 'tenantId' in loginApiData ? loginApiData.tenantId : '';
  const tenantId = selectedTenantId || ownTenantId;

  const { data: profile, isLoading, isError } = useGetBillingProfileQuery(tenantId, {
    skip: skip || !tenantId,
  });
  const [saveBillingProfile, { isLoading: isSaving }] = useSaveBillingProfileMutation();

  const missingFields = useMemo(() => getMissingBillingProfileFields(profile), [profile]);

  const save = useCallback(
    (values: BillingProfileFormValues) =>
      saveBillingProfile({
        tenantId,
        exists: !!profile,
        values: toBillingProfilePayload(values),
      }).unwrap(),
    [saveBillingProfile, tenantId, profile]
  );

  return {
    profile: profile ?? null,
    isLoading,
    isError,
    isSaving,
    /** True only once we know fields are missing; never true while loading or on a load error. */
    isIncomplete: !skip && !!tenantId && !isLoading && !isError && missingFields.length > 0,
    missingFields,
    save,
  };
}
